import { format, isValid, parse, startOfDay } from 'date-fns';

import {
  type ActivityPeriod,
  activityPeriod,
  type ActivityRange,
  type GetActivityParams,
} from '@/api/gamma-team/types';

export const DEFAULT_PERIOD: ActivityPeriod = activityPeriod.ThisMonth;

export const ALL_TEAM = 'all';

export const PERIOD_OPTIONS: { value: ActivityPeriod; label: string }[] = [
  { value: activityPeriod.ThisWeek, label: 'This week' },
  { value: activityPeriod.LastWeek, label: 'Last week' },
  { value: activityPeriod.ThisMonth, label: 'This month' },
  { value: activityPeriod.LastMonth, label: 'Last month' },
  { value: activityPeriod.Last30Days, label: 'Last 30 days' },
  { value: activityPeriod.ThisQuarter, label: 'This quarter' },
  { value: activityPeriod.Custom, label: 'Custom range' },
];

const ISO_DAY = 'yyyy-MM-dd';

const PERIOD_VALUES = new Set<string>(Object.values(activityPeriod));

export const isActivityPeriod = (raw: string | undefined): raw is ActivityPeriod =>
  raw !== undefined && PERIOD_VALUES.has(raw);

export const parsePeriod = (raw: string | undefined): ActivityPeriod =>
  isActivityPeriod(raw) ? raw : DEFAULT_PERIOD;

export const toIsoDay = (date: Date): string => format(date, ISO_DAY);

export const parseIsoDay = (raw: string | undefined): Date | null => {
  if (!raw) return null;
  const date = parse(raw, ISO_DAY, new Date());
  return isValid(date) && format(date, ISO_DAY) === raw ? date : null;
};

export const resolveTimeZone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

export interface ActivityFilters {
  period: ActivityPeriod;
  from: Date | null;
  to: Date | null;
}

export const toActivityParams = (
  { period, from, to }: ActivityFilters,
  timeZone: string,
): GetActivityParams | null => {
  if (period !== activityPeriod.Custom) return { period, timeZone };
  if (!from || !to || from.getTime() > to.getTime()) return null;
  return { period, from: toIsoDay(from), to: toIsoDay(to), timeZone };
};

export const ACTIVITY_DAY_FORMAT = 'MMM d, yyyy';

export interface ActivityDayRange {
  from: Date;
  to: Date;
}

export const toActivityDateRange = (range: ActivityRange): ActivityDayRange | null => {
  const from = new Date(range.from);
  const lastDay = new Date(Date.parse(range.to) - 1);
  if (!isValid(from) || !isValid(lastDay)) return null;
  return { from: startOfDay(from), to: startOfDay(lastDay) };
};

export const formatActivityRange = (range: ActivityRange): string => {
  const days = toActivityDateRange(range);
  if (!days) return '';
  return `${format(days.from, ACTIVITY_DAY_FORMAT)} – ${format(days.to, ACTIVITY_DAY_FORMAT)}`;
};
